import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import axios from "axios"
import moment from "moment"
import toast from "react-hot-toast"
import type { RootState } from "@/store/store"
import { selectFirstName, selectUserId } from "@/selectors/auth.selectors"
import type { OrderDetailsUI } from "@/domain/order/order-details.model"
import type { FetchOrderDetailsResult } from "@/services/order-details"
import { fetchOrderDetailsUI } from "@/services/order-details"
import { trackEvent } from "@/utils/analytics"
import { setBillingPreview } from "@/slices/final-billing-slice"
import { setCheckoutPhase } from "@/slices/checkout-ux-slice"
import { updateMenuItems } from "@/slices/menu-items-slice"
import { useCartStore } from "@/store/cart/cart-store"
import { readSafeCartItems } from "@/store/cart/cart.selectors"
import { selectAddresses } from "@/selectors/auth.selectors"
import {
  executeReorder,
} from "@/services/reorder.service"
import { buildReorderFailureAnalyticsPayload } from "@/domain/reorder/reorder.presentation"
import { type MemoryWriteDecision, useSafeQuery } from "@/hooks/use-safe-query"
import { orderDetailsIdleQueryKey, orderDetailsQueryKey } from "@/query-keys/orders.keys"
import { storefrontJoin, useStorefrontBasePath } from "@/utils/storefront-paths"
import { useEffectiveFulfilment } from "@/hooks/use-effective-fulfilment"

function orderDetailsMemoryWrite(result: FetchOrderDetailsResult): MemoryWriteDecision {
  if (result === "empty") return { write: false, ttlMs: undefined }
  return { write: result !== undefined && result !== null, ttlMs: undefined }
}

export type UseOrderDetailsPageResult = {
  fetching: boolean
  dependenciesPending: boolean
  hasValidOrderId: boolean
  resolvedEmpty: boolean
  resolvedSuccess: boolean
  order: OrderDetailsUI | null | "empty"
  /** Present when the details request failed (safe-query error). */
  fetchError: Error | undefined
  /** True when the failure was an unauthorized response (session likely expired). */
  isUnauthorized: boolean
  refresh: () => void
  refreshDisabled: boolean
  navigateBackToOrders: () => void
  onReorder: () => void
  onDownloadInvoice: () => void
  onRetryPaymentNavigate: () => void
  invoiceDownloadAvailable: boolean
  lastUpdatedLabel: string | null
  reorderBusy: boolean
  /** True after reorder preparation fails — show inline recovery panel. */
  reorderRecoveryVisible: boolean
  dismissReorderRecovery: () => void
  onReorderBrowseMenu: () => void
}

export function useOrderDetailsPage(orderId: string | undefined): UseOrderDetailsPageResult {
  const navigate = useNavigate()
  const basePath = useStorefrontBasePath()
  const dispatch = useDispatch()
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
  const [reorderBusy, setReorderBusy] = useState(false)
  const [reorderRecoveryVisible, setReorderRecoveryVisible] = useState(false)

  const oid = (orderId ?? "").trim()
  const hasValidOrderId = oid.length > 0
  const dependenciesPending = false

  const { data, error, isLoading, refetch } = useSafeQuery<FetchOrderDetailsResult>({
    queryKey: oid ? orderDetailsQueryKey(oid) : orderDetailsIdleQueryKey(),
    enabled: hasValidOrderId,
    cache: "memory",
    fetcher: async () => fetchOrderDetailsUI(oid),
    memoryWrite: orderDetailsMemoryWrite,
  })

  const menuItems = useSelector((s: RootState) => s.menuItems?.list ?? [])
  const coupons = useSelector((s: RootState) => s.discounts)
  const addresses = useSelector(selectAddresses)
  const { orderType } = useEffectiveFulfilment()
  const userId = useSelector(selectUserId)
  const firstName = useSelector(selectFirstName)

  const replaceItems = useCartStore((s) => s.replaceItems)
  const unlockCartStore = useCartStore((s) => s.unlockCart)
  const setCheckoutLockedStore = useCartStore((s) => s.setCheckoutLocked)
  const isLocked = useCartStore((s) => s.isLocked)
  const isCheckoutLocked = useCartStore((s) => s.isCheckoutLocked)

  const lastUpdatedLabel = useMemo(
    () => (lastUpdatedAt ? moment(lastUpdatedAt).fromNow() : null),
    [lastUpdatedAt]
  )

  const isUnauthorized = Boolean(axios.isAxiosError(error) && error.response?.status === 401)

  // Fetch failures / empty are owned by the order-details page EmptyData UI — no toast.

  useEffect(() => {
    if (data === undefined) return
    if (data === "empty") return
    setLastUpdatedAt(new Date())
  }, [data])

  const fetchError = error

  const order: OrderDetailsUI | null | "empty" =
    fetchError && !isLoading ? null : data !== undefined ? data : null

  const resolvedEmpty = data === "empty"
  const resolvedSuccess = data !== undefined && data !== "empty" && fetchError === undefined

  const fetching = dependenciesPending || (hasValidOrderId && isLoading)

  useEffect(() => {
    if (!data || data === "empty") return
    trackEvent("order_details_viewed", {
      orderId: data.orderId,
      status: data.statusSemantic.stage,
      paymentStatus: data.paymentStatus,
    })
  }, [data])

  const refresh = useCallback(() => {
    void refetch({ force: true })
  }, [refetch])

  const navigateBackToOrders = useCallback(() => {
    navigate(storefrontJoin(basePath, "/my-account/orders"))
  }, [navigate, basePath])

  const dismissReorderRecovery = useCallback(() => {
    setReorderRecoveryVisible(false)
  }, [])

  const onReorderBrowseMenu = useCallback(() => {
    setReorderRecoveryVisible(false)
    navigate(storefrontJoin(basePath, "/dishes"))
  }, [navigate, basePath])

  const onReorder = useCallback(async () => {
    if (!data || data === "empty") return
    if (!userId) {
      toast.error("Sign in to reorder.")
      return
    }
    setReorderBusy(true)
    setReorderRecoveryVisible(false)
    try {
      const defaultAddr = addresses.find((a) => a.isDefault)
      const result = await executeReorder({
        order: data,
        userId,
        firstName: firstName ?? "",
        coupons,
        fallbackMenuItems: menuItems,
        defaultAddressId: String(defaultAddr?.address_id ?? ""),
        orderType,
        getCartItems: () => readSafeCartItems(),
        replaceItems,
        unlockCart: unlockCartStore,
        setCheckoutLocked: setCheckoutLockedStore,
        isLocked,
        isCheckoutLocked,
        syncCatalogAuthority: (authority) => {
          dispatch(
            updateMenuItems({
              list: authority.list,
              catalogSnapshot: authority.catalogSnapshot,
            })
          )
        },
        syncMenuItemsWithCounts: (nextMenuItems) => {
          dispatch(updateMenuItems({ list: nextMenuItems }))
        },
        setBillingPreview: (preview) => {
          dispatch(setBillingPreview(preview))
        },
        setCheckoutPhase: (phase) => {
          dispatch(setCheckoutPhase(phase))
        },
        navigate: (path) => {
          navigate(path)
        },
      })

      if (result.status === "failure") {
        // Stay on order details — inline recovery panel (no toast / auto-nav).
        setReorderRecoveryVisible(true)
      }
    } catch {
      setReorderRecoveryVisible(true)
      trackEvent(
        "reorder_failure",
        buildReorderFailureAnalyticsPayload(data.orderId, "PREVIEW_FAILED")
      )
    } finally {
      setReorderBusy(false)
    }
  }, [
    data,
    userId,
    firstName,
    addresses,
    replaceItems,
    menuItems,
    dispatch,
    coupons,
    navigate,
    isCheckoutLocked,
    setCheckoutLockedStore,
    isLocked,
    unlockCartStore,
    orderType,
  ])

  const onDownloadInvoice = useCallback(() => {
    toast("Invoice download is not available for this order yet.")
  }, [])

  const onRetryPaymentNavigate = useCallback(() => {
    if (!data || data === "empty") return
    trackEvent("retry_payment_clicked", { orderId: data.orderId })
  }, [data])

  return {
    fetching,
    dependenciesPending,
    hasValidOrderId,
    resolvedEmpty,
    resolvedSuccess,
    order,
    fetchError,
    isUnauthorized,
    refresh,
    refreshDisabled: isLoading || !hasValidOrderId,
    navigateBackToOrders,
    onReorder,
    onDownloadInvoice,
    onRetryPaymentNavigate,
    invoiceDownloadAvailable: false,
    lastUpdatedLabel,
    reorderBusy,
    reorderRecoveryVisible,
    dismissReorderRecovery,
    onReorderBrowseMenu,
  }
}
